import React, { useState } from 'react';
import styled from 'styled-components';
import { MultiSelect } from 'components/FormFields';
import CodeBlock from '../../components/CodeBlock';
import ComponentDemo from '../../components/ComponentDemo';
import PropsTable from '../../components/PropsTable';

const PageTitle = styled.h1`
  font-size: 36px;
  font-weight: 700;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 16px 0;
`;

const PageDescription = styled.p`
  font-size: 18px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin: 0 0 48px 0;
  line-height: 1.6;
`;

const Section = styled.section`
  margin-bottom: 64px;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#F3F4F6' : '#1F2937')};
  margin: 0 0 24px 0;
`;

const SelectedValue = styled.div`
  margin-top: 16px;
  padding: 12px;
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#F9FAFB'};
  border-radius: 8px;
  font-size: 13px;
  color: ${(props) => (props.theme.name === 'dark' ? '#9CA3AF' : '#6B7280')};

  code {
    color: ${(props) => (props.theme.name === 'dark' ? '#F3F4F6' : '#1F2937')};
  }
`;

const categoryOptions = [
  { label: 'Racing', value: 'racing' },
  { label: 'Training', value: 'training' },
  { label: 'Exhibition', value: 'exhibition' },
  { label: 'Practice', value: 'practice' },
  { label: 'Qualifying', value: 'qualifying' },
  { label: 'Special Event', value: 'special' },
];

const trackOptions = [
  { label: 'Daytona International Speedway', value: 1 },
  { label: 'Indianapolis Motor Speedway', value: 2 },
  { label: 'Charlotte Motor Speedway', value: 3 },
  { label: 'Bristol Motor Speedway', value: 4 },
  { label: 'Talladega Superspeedway', value: 5 },
  { label: 'Martinsville Speedway', value: 6 },
  { label: 'Watkins Glen International', value: 7 },
  { label: 'Road America', value: 8 },
];

const MultiSelectDocs = () => {
  const [basicValue, setBasicValue] = useState([]);
  const [selectAllValue, setSelectAllValue] = useState([]);
  const [maxValue, setMaxValue] = useState([]);

  const props = [
    {
      name: 'label',
      type: 'string',
      required: false,
      description: 'Label text displayed above the select',
    },
    {
      name: 'required',
      type: 'boolean',
      default: 'false',
      required: false,
      description: 'Shows a red asterisk next to the label',
    },
    {
      name: 'value',
      type: 'array',
      default: '[]',
      required: false,
      description:
        'Array of selected values (strings, numbers, or {label, value} objects)',
    },
    {
      name: 'onChange',
      type: 'function',
      required: true,
      description:
        'Callback when selection changes. Receives array of selected option objects.',
    },
    {
      name: 'placeholder',
      type: 'string',
      default: '"Search to add..."',
      required: false,
      description: 'Placeholder text for the search input',
    },
    {
      name: 'options',
      type: 'array',
      default: '[]',
      required: false,
      description: 'Array of {label, value} objects',
    },
    {
      name: 'loading',
      type: 'boolean',
      default: 'false',
      required: false,
      description: 'Shows loading state in the dropdown',
    },
    {
      name: 'onSearch',
      type: 'function',
      required: false,
      description: 'Callback when search text changes (for async search)',
    },
    {
      name: 'minSearchLength',
      type: 'number',
      default: '0',
      required: false,
      description: 'Minimum characters before filtering begins',
    },
    {
      name: 'noResultsText',
      type: 'string',
      default: '"No results found"',
      required: false,
      description: 'Text shown when no options match',
    },
    {
      name: 'helperText',
      type: 'string',
      required: false,
      description: 'Helper or error text below the select',
    },
    {
      name: 'error',
      type: 'boolean',
      default: 'false',
      required: false,
      description: 'Shows error state styling',
    },
    {
      name: 'width',
      type: 'string',
      default: '"100%"',
      required: false,
      description: 'Width of the select field',
    },
    {
      name: 'maxWidth',
      type: 'string',
      required: false,
      description: 'Maximum width of the select field',
    },
    {
      name: 'disabled',
      type: 'boolean',
      default: 'false',
      required: false,
      description: 'Disables the select',
    },
    {
      name: 'selectedLabel',
      type: 'string',
      default: '"Selected"',
      required: false,
      description: 'Label for the selected items section',
    },
    {
      name: 'showSelectAll',
      type: 'boolean',
      default: 'false',
      required: false,
      description: 'Shows a "Select All" option at the top of the dropdown',
    },
    {
      name: 'maxSelected',
      type: 'number',
      required: false,
      description:
        'Maximum number of selections allowed. Options become disabled when limit is reached.',
    },
  ];

  return (
    <>
      <PageTitle>MultiSelect</PageTitle>
      <PageDescription>
        A multi-select dropdown with a split view design. Selected items are
        displayed in a separate section below the input with individual remove
        buttons. Supports search filtering, keyboard navigation, Select All, and
        maximum selection limits.
      </PageDescription>

      <Section>
        <SectionTitle>Props</SectionTitle>
        <PropsTable props={props} />
      </Section>

      <Section>
        <SectionTitle>Basic MultiSelect</SectionTitle>
        <ComponentDemo label="Select multiple categories" align="stretch">
          <div style={{ width: '400px', maxWidth: '100%' }}>
            <MultiSelect
              label="Event Categories"
              placeholder="Search categories..."
              options={categoryOptions}
              value={basicValue}
              onChange={setBasicValue}
            />
            {basicValue.length > 0 && (
              <SelectedValue>
                Selected:{' '}
                <code>{basicValue.map((v) => v.label || v).join(', ')}</code>
              </SelectedValue>
            )}
          </div>
        </ComponentDemo>

        <CodeBlock>
          {`import { MultiSelect } from 'components/FormFields';

const [selected, setSelected] = useState([]);

<MultiSelect
  label="Event Categories"
  placeholder="Search categories..."
  options={categoryOptions}
  value={selected}
  onChange={setSelected}
/>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>With Select All</SectionTitle>
        <ComponentDemo label="Select All option in dropdown" align="stretch">
          <div style={{ width: '400px', maxWidth: '100%' }}>
            <MultiSelect
              label="Tracks"
              placeholder="Search tracks..."
              options={trackOptions}
              value={selectAllValue}
              onChange={setSelectAllValue}
              showSelectAll
              selectedLabel="Assigned Tracks"
            />
          </div>
        </ComponentDemo>

        <CodeBlock>
          {`<MultiSelect
  label="Tracks"
  options={trackOptions}
  value={selected}
  onChange={setSelected}
  showSelectAll
  selectedLabel="Assigned Tracks"
/>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>With Max Selection Limit</SectionTitle>
        <ComponentDemo label="Maximum 3 selections" align="stretch">
          <div style={{ width: '400px', maxWidth: '100%' }}>
            <MultiSelect
              label="Top 3 Tracks"
              placeholder="Select up to 3..."
              options={trackOptions}
              value={maxValue}
              onChange={setMaxValue}
              maxSelected={3}
              helperText="Select your top 3 favorite tracks"
            />
          </div>
        </ComponentDemo>

        <CodeBlock>
          {`<MultiSelect
  label="Top 3 Tracks"
  placeholder="Select up to 3..."
  options={trackOptions}
  value={selected}
  onChange={setSelected}
  maxSelected={3}
  helperText="Select your top 3 favorite tracks"
/>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>With Error State</SectionTitle>
        <ComponentDemo label="Error validation" align="stretch">
          <div style={{ width: '400px', maxWidth: '100%' }}>
            <MultiSelect
              label="Required Categories"
              required
              placeholder="Search categories..."
              options={categoryOptions}
              value={[]}
              onChange={() => undefined}
              error
              helperText="Please select at least one category"
            />
          </div>
        </ComponentDemo>
      </Section>

      <Section>
        <SectionTitle>Import</SectionTitle>
        <CodeBlock>
          {`import { MultiSelect } from 'components/FormFields';
// or
import MultiSelect from 'components/FormFields/MultiSelect';`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Features</SectionTitle>
        <ul style={{ lineHeight: 1.8 }}>
          <li>
            Split view: dropdown for adding, list below for managing selections
          </li>
          <li>Search filtering with keyboard navigation</li>
          <li>
            Optional &quot;Select All&quot; / &quot;Deselect All&quot; toggle
          </li>
          <li>Maximum selection limit with visual feedback</li>
          <li>Count badge showing number of selected items</li>
          <li>Individual remove buttons on selected items</li>
          <li>Theme-aware (light/dark mode)</li>
          <li>Accessible with ARIA attributes</li>
        </ul>
      </Section>
    </>
  );
};

export default MultiSelectDocs;
